/**
 * 型安全なクライアント i18n。
 * キーの正は ja。言語追加は Locale・カタログ・セレクト表示名の3箇所。
 */

import { en } from './locales/en';
import { ja, type Messages } from './locales/ja';

export const LOCALES = ['ja', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_LABELS: Record<Locale, string> = {
  ja: '日本語',
  en: 'English',
};

export const LOCALE_STORAGE_KEY = 'mapeditor:locale';

export type { Messages };
export type MessageVars = Record<string, string | number>;

type MessageKey<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? Prefix extends ''
      ? K
      : `${Prefix}.${K}`
    : MessageKey<T[K], Prefix extends '' ? K : `${Prefix}.${K}`>;
}[keyof T & string];

export type MessagePath = MessageKey<Messages>;

export const DENOISER_MODE_PATHS = {
  off: 'graphics.denoiserOff',
  temporal: 'graphics.denoiserTemporal',
  svgf: 'graphics.denoiserSvgf',
  'svgf-sharp': 'graphics.denoiserSvgfSharp',
  relax: 'graphics.denoiserRelax',
} as const satisfies Record<string, MessagePath>;

const CATALOGS: Record<Locale, Messages> = { ja, en };

let current: Locale = 'ja';
const listeners = new Set<(locale: Locale) => void>();

export function isLocale(value: string | null | undefined): value is Locale {
  return value === 'ja' || value === 'en';
}

export function getLocale(): Locale {
  return current;
}

export function detectLocale(input: {
  stored?: string | null;
  languages?: readonly string[];
}): Locale {
  if (isLocale(input.stored)) return input.stored;
  for (const language of input.languages ?? []) {
    const tag = language.toLowerCase();
    if (tag === 'en' || tag.startsWith('en-')) return 'en';
    if (tag === 'ja' || tag.startsWith('ja-')) return 'ja';
  }
  return 'ja';
}

export function setLocale(locale: Locale): void {
  current = locale;
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
    document.title = t('meta.title');
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  }
  for (const listener of listeners) listener(locale);
}

export function subscribeLocale(listener: (locale: Locale) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function t(key: MessagePath, vars?: MessageVars): string {
  return translate(key, vars);
}

export function translate(key: string, vars?: MessageVars, fallback?: string): string {
  const resolved =
    lookup(CATALOGS[current], key) ?? lookup(ja, key) ?? fallback ?? key;
  return interpolate(resolved, vars);
}

export function formatLocaleNumber(value: number): string {
  return value.toLocaleString(current === 'en' ? 'en' : 'ja');
}

export function materialLabel(id: string, fallback: string): string {
  return translate(`material.${id}`, undefined, fallback);
}

export function packLabel(id: string, fallback: string): string {
  return translate(`pack.${id}`, undefined, fallback);
}

export function objectLabel(id: string, fallback = id): string {
  return translate(`object.${id}`, undefined, fallback);
}

export function collectMessageKeys(messages: object, prefix = ''): string[] {
  const keys: string[] = [];
  for (const [name, value] of Object.entries(messages)) {
    const path = prefix ? `${prefix}.${name}` : name;
    if (typeof value === 'string') keys.push(path);
    else if (value && typeof value === 'object') keys.push(...collectMessageKeys(value, path));
  }
  return keys;
}

export function messageCatalog(locale: Locale): Messages {
  return CATALOGS[locale];
}

function lookup(messages: object, key: string): string | undefined {
  let current: unknown = messages;
  for (const part of key.split('.')) {
    if (typeof current !== 'object' || current === null || !(part in current)) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

function interpolate(template: string, vars?: MessageVars): string {
  if (!vars) return template;
  return template.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (match, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
}
